import React, { useEffect } from 'react';

const sizeMap = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-6xl',
};

export default function Modal({
    open,
    onClose,
    title,
    description,
    size = 'lg',
    fullHeight = false,
    fullScreen = false,
    centered = false,
    className = '',
    children,
}) {
    useEffect(() => {
        if (!open) return;
        const handler = (e) => e.key === 'Escape' && onClose?.();
        document.addEventListener('keydown', handler);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handler);
            document.body.style.overflow = '';
        };
    }, [open, onClose]);

    if (!open) return null;

    if (fullScreen) {
        return (
            <div className="fixed inset-0 z-50 flex flex-col bg-white">
                {title && (
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
                            {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )}
                <div className="overflow-x-hidden overflow-y-auto flex-1 min-w-0">
                    {children}
                </div>
            </div>
        );
    }

    const heightCls = fullHeight ? 'h-[calc(100vh-4rem)]' : 'max-h-[90vh]';
    const alignCls = fullHeight || centered ? 'items-center' : 'items-start pt-[5vh]';

    return (
        <div className={`fixed inset-0 z-50 flex justify-center ${alignCls}`}>
            <div className="fixed inset-0 bg-black/50" onClick={onClose} />
            <div className={`relative ${sizeMap[size] || sizeMap.lg} w-full mx-4 bg-white rounded-xl border border-gray-200 shadow-xl flex flex-col ${heightCls} ${className}`}>
                {title && (
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
                            {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )}
                <div className="overflow-x-hidden overflow-y-auto flex-1 min-w-0">
                    {children}
                </div>
            </div>
        </div>
    );
}

Modal.Body = function ModalBody({ children, className = '' }) {
    return <div className={`p-6 space-y-4 ${className}`}>{children}</div>;
};

Modal.Footer = function ModalFooter({ children, className = '' }) {
    return (
        <div className={`flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl ${className}`}>
            {children}
        </div>
    );
};
